import { AnonUserCriteria } from 'modules/graphql/types/GraphQLAnonymousCriteria';
import { Archive24 } from 'ui/icons/aurora/24px/Archive24';
import { Start24 } from 'ui/icons/aurora/24px/Start24';
import { Stop24 } from 'ui/icons/aurora/24px/Stop24';
import { AllowedActionMenuElements } from 'ui/actionMenu/types';
import { Tooltip } from 'ui/tooltip/Tooltip';
import { PropsWithChildren } from 'react';
import { CONTAINS_VALID_HANDLEBAR } from 'utils/validationRegex';

import {
  ActionsCell,
  ActivityCell,
  LastModifiedCell,
  DetailsCell,
  StatusCell,
} from '../components/AnonymousCriteriaTable';
import { MAX_LIVE_CRITERIA_COUNT, defaultSearchQuery } from '../constants';
import { CriteriaCreateFormState, CriteriaTableColumn } from '../types';

export const getCriteriaDefaultValues = (): CriteriaCreateFormState => {
  return {
    searchQuery: defaultSearchQuery,
    name: '',
    description: '',
    isLive: 'true',
  };
};

export const createTableRow = (
  criteria: AnonUserCriteria,
  setIsArchivalModalOpen: (open: boolean) => void,
  setIsToggleActiveModalOpen: (open: boolean) => void,
  setActionModalCriteria: (criteria: AnonUserCriteria) => void,
) => ({
  [CriteriaTableColumn.ANONYMOUS_CRITERIA]: <DetailsCell criteria={criteria} />,
  [CriteriaTableColumn.STATUS]: <StatusCell criteria={criteria} />,
  [CriteriaTableColumn.ACTIVITY]: <ActivityCell criteria={criteria} />,
  [CriteriaTableColumn.LAST_MODIFIED]: <LastModifiedCell criteria={criteria} />,
  [CriteriaTableColumn.ACTIONS]: (
    <ActionsCell
      criteria={criteria}
      setIsArchivalModalOpen={setIsArchivalModalOpen}
      setIsToggleActiveModalOpen={setIsToggleActiveModalOpen}
      setActionModalCriteria={setActionModalCriteria}
    />
  ),
});

export const createActivateOption = ({
  criteria,
  setIsToggleActiveModalOpen,
  setActionModalCriteria,
  maxActiveCriteriaLimitReached,
}: {
  criteria: AnonUserCriteria;
  setIsToggleActiveModalOpen: (open: boolean) => void;
  setActionModalCriteria?: (criteria: AnonUserCriteria) => void;
  maxActiveCriteriaLimitReached: boolean;
}) => {
  const ActivateIcon = criteria.fields.isLive ? Stop24 : Start24;
  const activateLabel = criteria.fields.isLive ? 'Turn off' : 'Turn on';
  const activateDisabled = maxActiveCriteriaLimitReached && !criteria.fields.isLive;

  return {
    id: 'toggle-criteria',
    content: (
      <>
        <ActivateIcon />
        {activateLabel}
      </>
    ),
    as: AllowedActionMenuElements.Button,
    onClick: () => {
      setIsToggleActiveModalOpen(true);
      setActionModalCriteria?.(criteria);
    },
    isDisabled: activateDisabled,
    Wrapper: ({ children }: PropsWithChildren) => (
      <Tooltip
        isEnabled={activateDisabled}
        tooltipContent={`Max of ${MAX_LIVE_CRITERIA_COUNT} live criteria`}
      >
        {children}
      </Tooltip>
    ),
  };
};

export const createArchiveOption = ({
  criteria,
  setIsArchivalModalOpen,
  setActionModalCriteria,
}: {
  criteria: AnonUserCriteria;
  setIsArchivalModalOpen: (open: boolean) => void;
  setActionModalCriteria?: (criteria: AnonUserCriteria) => void;
}) => {
  const archiveLabel = criteria.isArchived ? 'Unarchive' : 'Archive';

  return {
    id: 'toggle-archival',
    content: (
      <>
        <Archive24 />
        {archiveLabel}
      </>
    ),
    as: AllowedActionMenuElements.Button,
    onClick: () => {
      setIsArchivalModalOpen(true);
      setActionModalCriteria?.(criteria);
    },
  };
};

export const containsMergeParam = (str: string | undefined) => {
  if (!str) return false;
  return CONTAINS_VALID_HANDLEBAR.test(str);
};

export const isValidRegex = (regexStr: string | undefined) => {
  if (!regexStr) return true;
  try {
    const regex = new RegExp(regexStr);
    return true;
  } catch (e) {
    return false;
  }
};
